export interface LeaHistoryEntry {
    role: "lead" | "assistant" | "staff";
    text: string;
    at?: string;
}

export interface LeaWorkflow {
    mode: "manual" | "auto";
    status: string;
    blocked_reason: string | null;
    opted_out?: boolean;
    staff_takeover: boolean;
    history: LeaHistoryEntry[];
    outbound_enabled: boolean;
    test_recipient: string | null;
}

export interface LeaStatusSummary {
    label: string;
    tone: "active" | "warning" | "neutral";
    canTakeOver: boolean;
}

const REASON_LABELS: Record<string, string> = {
    staffed_hours: "Waiting for off hours",
    sensitive_reply: "Reply needs staff review",
    send_result_uncertain: "SMS outcome is uncertain",
    send_outcome_unverified: "SMS outcome was not verified",
    send_cancelled: "Send was cancelled",
    generation_failed: "Lea AI could not write a reply",
    generation_interrupted: "Lea AI was interrupted",
    staff_help_needed: "Lea AI asked for staff help",
    no_safe_response: "Lea AI had no safe reply",
    policy_unconfigured: "Contact hours are not configured",
    draft_disabled: "Lea AI is disabled",
    no_sms_consent: "No SMS consent",
    self_pay: "Self-pay inquiry",
    blacklisted: "Blocked from SMS contact",
    ineligible_status: "Inquiry status is not eligible",
    inquiry_missing: "Inquiry is unavailable",
};

export function leaReasonLabel(reason: string | null | undefined): string | null {
    if (!reason) {
        return null;
    }
    return REASON_LABELS[reason] ?? reason.replace(/_/g, " ");
}

export interface LeaGuidance {
    type: "info" | "warning";
    text: string;
}

/**
 * True once staff ended a test whose last SMS was never verified. The backend
 * keeps `send_unknown` but has already closed the session.
 */
export function leaUnverifiedSessionEnded(workflow: LeaWorkflow): boolean {
    return workflow.blocked_reason === "send_outcome_unverified";
}

/** Tells staff what the current state means and what to do next. */
export function leaGuidance(workflow: LeaWorkflow): LeaGuidance | null {
    if (leaUnverifiedSessionEnded(workflow)) {
        return {
            type: "warning",
            text:
                "The test session ended without confirming Lea AI's last SMS. This " +
                "inquiry and test number stay locked. Check the RingCentral message " +
                "log before contacting the client.",
        };
    }
    if (["sending", "send_unknown"].includes(workflow.status)) {
        return {
            type: "warning",
            text:
                "Lea AI may not have delivered its last SMS. Check the RingCentral " +
                "message log for this number before contacting the client, then " +
                "end the test session.",
        };
    }
    if (workflow.blocked_reason === "sensitive_reply") {
        return {
            type: "warning",
            text:
                "The client's last reply needs staff review. Lea AI has stopped " +
                "responding, so follow up with the client directly.",
        };
    }
    if (workflow.blocked_reason === "staffed_hours" && workflow.mode === "auto") {
        return {
            type: "info",
            text: "Lea AI will send its next message once off hours begin.",
        };
    }
    const reason = leaReasonLabel(workflow.blocked_reason);
    return reason ? { type: "info", text: `${reason}.` } : null;
}

export function leaStatusSummary(workflow: LeaWorkflow): LeaStatusSummary {
    if (["sending", "send_unknown"].includes(workflow.status)) {
        return { label: "Needs review", tone: "warning", canTakeOver: false };
    }
    if (workflow.status === "stopped") {
        return {
            label: workflow.opted_out ? "Opted out" : "Stopped",
            tone: "neutral",
            canTakeOver: false,
        };
    }
    if (workflow.status === "completed") {
        return { label: "Booking link sent", tone: "neutral", canTakeOver: false };
    }
    if (
        workflow.staff_takeover ||
        workflow.mode !== "auto" ||
        ["paused", "blocked"].includes(workflow.status)
    ) {
        return { label: "Intake handling", tone: "neutral", canTakeOver: false };
    }
    const hasSent = workflow.history.some((entry) => entry.role === "assistant");
    return {
        label: hasSent ? "Handling" : "Queued, no SMS sent yet",
        tone: "active",
        canTakeOver: true,
    };
}
